import * as xc from './xcDistance';

const ellipsoid = require('ayvri-std/lib/ellipsoid');

const round = (x, n) => {
  if (x === 0) return 0;
  const fixed = x.toFixed(n);
  // toFixed can return -0, it's true :( 0.3.toFixed(0) = -0;
  return fixed === '-0' ? 0 : fixed;
};

// List of used constants
const MS_TO_KMH = 3.6;
const KM_TO_MI = 0.621371;
const M_TO_KM = 0.001;
const M_TO_FT = 3.28084;
const MS_TO_S = 0.001;
const MS_TO_KT = 1.94384;
const MIN_TO_MS = 60000;
const MS_TO_FTM = 196.85;

export const measures = {
  speed: {
    label: 'Speed',
    calc: p => p.speed,
    channel: 'speed',
    metric: {
      calc: s => round(s * MS_TO_KMH, 1),
      units: 'km/h',
    },
    imperial: {
      calc: s => round(s * MS_TO_KMH * KM_TO_MI, 1),
      units: 'mph',
    },
    functor: (point, context) => {
      if (context) {
        point.speed = context.distance / context.dt;
        point.speed = rcLP(context.prevPoint, point, 'speed', rcForF(0.003));
      } else {
        point.speed = 0;
      }
    },
  },
  boatSpeed: {
    label: 'Boat Speed',
    calc: p => round(p.speed * MS_TO_KT, 2),
    channel: 'speed',
    units: 'kt',
  },
  airSpeed: {
    label: 'Air Speed',
    calc: p => round(p.speed * MS_TO_KT, 2),
    channel: 'speed',
    units: 'kt',
  },
  distance: {
    label: 'Distance',
    calc: p => p.cumulative * M_TO_KM,
    channel: 'cumulative',
    metric: {
      calc: s => round(s, 2),
      units: 'km',
    },
    imperial: {
      calc: s => round(s * KM_TO_MI, 2),
      units: 'mi',
    },
  },
  gradient: {
    label: 'Gradient',
    calc: p => round(p.gradient, 1),
    channel: 'gradient',
    units: '%',
    functor: (point, context) => {
      if (!context) {
        point.gradient = 0;
        return;
      }

      const distance = context.distance;
      const height = point.originalHeight - context.prevPoint.originalHeight;
      if (distance === 0) {
        point.gradient = 9999;
      } else {
        // Gradient is defined as rise/run * 100
        point.gradient = (height / distance) * 100.0;
      }
    },
  },
  climbRate: {
    label: 'Climb Rate',
    calc: p => p.climbRate,
    channel: 'climbRate',
    metric: {
      calc: s => round(s, 2),
      units: 'm/s',
    },
    imperial: {
      calc: s => round(s * MS_TO_FTM, 2),
      units: 'ft/min',
    },
    functor: (point, context) => {
      if (!context) {
        point.climbRate = 0;
        return;
      }

      const changeInHeight = point.originalHeight - context.prevPoint.originalHeight;
      const changeInTime = Math.abs(point.time - context.prevPoint.time) * MS_TO_S;
      if (changeInTime === 0) {
        point.climbRate = 9999;
      } else {
        point.climbRate = changeInHeight / changeInTime;
      }
    },
  },
  totalAscent: {
    label: 'Total Ascent',
    calc: p => p.totalAscent,
    channel: 'totalAscent',
    metric: {
      calc: s => round(s, 0),
      units: 'm',
    },
    imperial: {
      calc: s => round(s * M_TO_FT, 0),
      units: 'ft',
    },
    functor: (point, context) => {
      if (!context) {
        point.totalAscent = 0;
        return;
      }

      const changeInHeight = point.height - context.prevPoint.height;
      point.totalAscent = context.prevPoint.totalAscent;
      if (changeInHeight > 0) point.totalAscent += changeInHeight;
    },
  },
  totalDescent: {
    label: 'Total Descent',
    calc: p => p.totalDescent,
    channel: 'totalDescent',
    metric: {
      calc: s => round(s, 0),
      units: 'm',
    },
    imperial: {
      calc: s => round(s * M_TO_FT, 0),
      units: 'ft',
    },
    functor: (point, context) => {
      if (!context) {
        point.totalDescent = 0;
        return;
      }

      const changeInHeight = point.height - context.prevPoint.height;
      point.totalDescent = context.prevPoint.totalDescent;
      if (changeInHeight < 0) point.totalDescent -= changeInHeight;
    },
  },
  glideRatio: {
    label: 'Glide Ratio',
    calc: p => {
      if (p.glideRatio < 0) {
        return '+++';
      } else if (p.glideRatio > 100) {
        return '---';
      } else {
        return round(p.glideRatio, 0);
      }
    },
    units: 'pts',
    channel: 'glideRatio',
    functor: (point, context) => {
      if (!context) {
        point.glideRatio = 0;
        return;
      }

      const distance = point.distance;
      const height = point.originalHeight - context.prevPoint.originalHeight;
      if (height === 0) {
        point.glideRatio = 9999.0;
      } else {
        point.glideRatio = -distance / height;
      }
    },
  },
  elevation: {
    label: 'Elevation',
    // elevation and altitude are the same formulas, but different labels for ground vs air
    calc: p => p.originalHeight,
    channel: 'originalHeight',
    metric: {
      calc: s => round(s, 0),
      units: 'm',
    },
    imperial: {
      calc: s => round(s * 3.28084, 0),
      units: 'ft',
    },
  },
  altitude: {
    label: 'Altitude',
    calc: p => p.originalHeight,
    channel: 'originalHeight',
    metric: {
      calc: s => round(s, 0),
      units: 'm',
    },
    imperial: {
      calc: s => round(s * M_TO_FT, 0),
      units: 'ft',
    },
  },
  altitudeFromStart: {
    label: 'Altitude from Start',
    calc: (p, points, state) => {
      const trackName = state.tracks.activeTrack;
      const startAltitude = state.tracks.tracks[trackName].originalHeight.first;
      return p.originalHeight - startAltitude;
    },
    channel: 'originalHeight',
    metric: {
      calc: s => round(s, 0),
      units: 'm',
    },
    imperial: {
      calc: s => round(s * M_TO_FT, 0),
      units: 'ft',
    },
  },
  gmtTime: {
    label: 'GMT Time',
    toFixed: 0,
    units: state => `${formatDateString(new Date(state.clock.time))}`,
    calc: (p, points, state) => `${formatTimeString(new Date(state.clock.time))} GMT`,
  },
  localTime: {
    label: 'Local Time',
    units: state => {
      const gmtPointDate = new Date(state.clock.time);
      if (state.clock.gmtOffset) {
        return formatDateString(new Date(gmtPointDate.getTime() + state.clock.gmtOffset * MIN_TO_MS));
      } else {
        return formatDateString(gmtPointDate);
      }
    },
    calc: (point, points, state) => {
      const gmtPointDate = new Date(state.clock.time);
      if (state.clock.gmtOffset) {
        return formatTimeString(new Date(gmtPointDate.getTime() + state.clock.gmtOffset * MIN_TO_MS));
      } else {
        return formatTimeString(gmtPointDate);
      }
    },
  },
  xcDistance: {
    label: 'XC Distance',
    calc: p => p.xcDistance,
    channel: 'xcDistance',
    metric: {
      calc: s => round(s * M_TO_KM, 2),
      units: 'km',
    },
    imperial: {
      calc: s => round(s * M_TO_KM * KM_TO_MI, 2),
      units: 'mi',
    },
    functor: (point, context) => {
      xc.applyXcStat(point, context);
    },
  },
  /** Custom Channels **/
  aeroVisionAltHeight: {
    label: 'Height Above Base',
    calc: p => p.aeroVisionAltHeight,
    channel: 'aeroVisionAltHeight',
    metric: {
      calc: s => round(s, 0),
      units: 'm',
    },
    imperial: {
      calc: s => round(s * M_TO_FT, 0),
      units: 'ft',
    },
    functor: (newPoint, _, oldPoint) => {
      newPoint.aeroVisionAltHeight = oldPoint.originalHeight - oldPoint.aeroVisionAltHeight;
    },
  },
};

// Takes in a javascript time object and returns a string in the from HH:MM
export const formatTimeString = date =>
  date
    .toUTCString()
    .split(' ')
    .slice(-2, -1)[0]
    .slice(0, 5);

export const formatDateString = date =>
  date
    .toUTCString()
    .split(' ')
    .slice(0, 4)
    .map(s => s.charAt(0).toUpperCase() + s.substring(1))
    .join(' ');

export const distanceBetweenPoints = (coords1, coords2) => {
  const p0 = ellipsoid.cartographicToCartesian(coords1.latitude, coords1.longitude, 0);
  const p1 = ellipsoid.cartographicToCartesian(coords2.latitude, coords2.longitude, 0);
  const distance = ellipsoid.dist(p0, p1);
  return distance;
};

// frequency to smooth to
const rcForF = hz => 1 / (hz * 2 * Math.PI);

// low pass filter for smoothing out point values
export const rcLP = (last, point, key, RC) => {
  const k = `__RC_STATE_${key}`;

  if (typeof last[k] === 'undefined') {
    last[k] = last[key];
  }

  const dt = Math.abs(last.time - point.time);
  const alpha = dt / (RC + dt);
  const y = alpha * point[key] + (1 - alpha) * last[k];
  point[k] = y;
  return y;
};

export const processPoints = (context = {}, points, trackChannels, altitudeOffset = 0.0) => {
  const newPoints = [];

  if (!context.lastPoint) {
    newPoints[0] = {
      ...points[0],
      distance: 0,
      speed: 0,
      height: points[0].height + altitudeOffset,
      cumulative: 0,
    };

    // Initialises the stat list with zero values
    trackChannels.forEach(channel => {
      if (measures[channel] && measures[channel].functor) {
        measures[channel].functor(newPoints[0], undefined, points[0]);
      }
    });
  }

  for (let i = context.lastPoint ? 0 : 1; i < points.length; i++) {
    context.prevPoint = i === 0 ? context.lastPoint : newPoints[i - 1];
    context.distance = distanceBetweenPoints(context.prevPoint, points[i]);
    context.dt = (points[i].time - context.prevPoint.time) * MS_TO_S;

    newPoints[i] = {};
    // Units are in radians for lat long
    newPoints[i].latitude = points[i].latitude;
    newPoints[i].longitude = points[i].longitude;
    newPoints[i].time = points[i].time;
    newPoints[i].distance = context.distance;
    newPoints[i].cumulative = context.prevPoint.cumulative + context.distance;
    newPoints[i].height = points[i].height + altitudeOffset;
    newPoints[i].terrain = points[i].terrain;
    newPoints[i].originalHeight = points[i].originalHeight;
    newPoints[i].depth = points[i].depth;

    trackChannels.forEach(channel => {
      if (newPoints[i][channel] === undefined && measures[channel]) {
        // Initialises the functor of the required channel
        measures[channel].functor(newPoints[i], context, points[i]);
      }
    });
  }

  context.lastPoint = newPoints[newPoints.length - 1];
  return newPoints;
};
